import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {AnyObject, CAP_DIRECTION, PIN_DIRECTION} from '@/globals';
import {
    CableCoreModel,
    CableModel,
    CreateEditCableCoreViewModel,
    CreateEditCableViewModel,
    EquipmentCapModel,
    EquipmentPinModel,
    LocationConnectionModel,
    LocationModel,
} from '@/sdk';
import {CableCoresApi, CablesApi, LocationApi, LocationConnectionApi} from '@/sdk/api-services';
import CableDetailsFormComponent from '@/views/shared/detail-forms/cable-details-form/cable-details-form.component';
import CoreDetailsFormComponent from '@/views/shared/detail-forms/core-details-form/core-details-form.component';
import PinDetailsFormComponent from '@/views/shared/detail-forms/pin-details-form/pin-details-form.component';
import {SiteLocationService} from '@/views/shared/services/site-location.service';
import go from 'gojs';
import {Component, Prop} from 'vue-property-decorator';
import {createConnectionPinsNodeTemplate} from './connection-diagram-utils';
import SelectCapDialogComponent from './select-cap-dialog/select-cap-dialog.component';

const $ = go.GraphObject.make;

@Component({
    components: {
        CableDetailsFormComponent,
        CoreDetailsFormComponent,
        PinDetailsFormComponent,
        SelectCapDialogComponent,
    },
})
/**
 * Connection Circuit Diagram Component
 *
 * This component handles the connection diagram visualization with link validation.
 * Link validation rules:
 * 1. Cores cannot connect to other cores
 * 2. Cores cannot connect to themselves
 * 3. Duplicate connections are prevented
 *
 * Link management:
 * - When data is loaded, creates links based on existing core pin associations (equipmentInPinId/equipmentOutPinId)
 * - When a core-pin link is drawn, updates the core's equipmentInPinId or equipmentOutPinId
 * - When a core-pin link is deleted, clears the core's pin associations
 * - All changes are persisted to the server via API calls
 */
export default class ConnectionCircuitDiagramComponent extends VueWrapper {
    public $refs!: {
        selectCapDialogRef: SelectCapDialogComponent;
    };

    @Prop({
        type: String,
        required: true,
    })
    public readonly selectedConnectionId!: string;

    @Prop({
        type: Boolean,
        default: false,
    })
    public readonly readonly!: boolean;

    public locationApi: LocationApi = new LocationApi();
    public connectionApi: LocationConnectionApi = new LocationConnectionApi();
    public cablesApi: CablesApi = new CablesApi();
    public cableCoresApi: CableCoresApi = new CableCoresApi();
    public siteLocationsSrv: SiteLocationService = new SiteLocationService();

    public get hasCircuitEditPermission(): boolean {
        return this.$helpers.hasPermission(this.$helpers.PERMISSION_KEYS.CIRCUIT_EDIT, this.siteLocationsSrv.site?.id ?? null);
    }

    public get isReadonly(): boolean {
        return !this.hasCircuitEditPermission || this.readonly;
    }

    public connection: LocationConnectionModel | null = null;
    public fromLocation: LocationModel | null = null;
    public toLocation: LocationModel | null = null;

    private myDiagram: go.Diagram | null = null;

    private fromLocationOutPins: EquipmentPinModel[] = [];
    private toLocationInPins: EquipmentPinModel[] = [];

    public fromLocationOutCaps: EquipmentCapModel[] = [];
    public toLocationInCaps: EquipmentCapModel[] = [];

    private fromLocationOutCables: AnyObject[] = [];
    private toLocationInCables: AnyObject[] = [];

    private fromLocationOutCores: AnyObject[] = [];
    private toLocationInCores: AnyObject[] = [];

    private cables: CableModel[] = [];
    private noCableCores: CableCoreModel[] = [];

    // Cable form related properties
    public selectedCable: CableModel | null = null;

    // Core form related properties
    public selectedCore: CableCoreModel | null = null;

    // Pin form related properties
    public selectedPin: EquipmentPinModel | null = null;

    // Cap selection tracking
    public selectedCaps: {[cableId: string]: {inCap?: EquipmentCapModel; outCap?: EquipmentCapModel}} = {};

    // Getters for accessing private properties in template
    public get availableFromCaps(): EquipmentCapModel[] {
        return this.fromLocationOutCaps;
    }

    public get availableToCaps(): EquipmentCapModel[] {
        return this.toLocationInCaps;
    }

    public get availableCables(): CableModel[] {
        return this.cables;
    }

    public mounted(): void {
        // Listen for cap selection events
        this.$root.$on('cap-selected', this.onCapSelected.bind(this));
        this.$root.$on('cap-added', this.onCapAdded.bind(this));
        this.$root.$on('cap-deleted', this.onCapDeleted.bind(this));

        new Promise(resolve => {
            this.initializeGoJsDiagram();
            setTimeout(() => {
                resolve(null);
            }, 100);
        }).then(() => {
            this.getLocations();
        });
    }

    public async getLocations(): Promise<void> {
        this.connectionApi
            .getLocationConnectionByIdAsync(this.selectedConnectionId, [
                {
                    relation: 'cables',
                    scope: {
                        include: [
                            {
                                relation: 'equipmentInCap',
                            },
                            {
                                relation: 'equipmentOutCap',
                            },
                            {
                                relation: 'cableCores',
                            },
                            {
                                relation: 'locationConnection',
                                scope: {
                                    include: [
                                        {
                                            relation: 'fromLocation',
                                        },
                                        {
                                            relation: 'toLocation',
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                },
                {
                    relation: 'noCableCores',
                    scope: {
                        include: [
                            {
                                relation: 'equipmentInCap',
                            },
                            {
                                relation: 'equipmentOutCap',
                            },
                        ],
                        where: {
                            cableId: {
                                eq: null,
                            },
                        },
                    },
                },
                {
                    relation: 'fromLocation',
                    scope: {
                        include: [
                            {
                                relation: 'equipmentDetail',
                            },
                            {
                                relation: 'equipmentPins',
                            },
                            {
                                relation: 'equipmentCaps',
                            },
                            {
                                relation: 'equipmentConnectorPins',
                            },
                        ],
                    },
                },
                {
                    relation: 'toLocation',
                    scope: {
                        include: [
                            {
                                relation: 'equipmentDetail',
                            },
                            {
                                relation: 'equipmentPins',
                            },
                            {
                                relation: 'equipmentCaps',
                            },
                            {
                                relation: 'equipmentConnectorPins',
                            },
                        ],
                    },
                },
            ])
            .then(async (connection: LocationConnectionModel) => {
                this.connection = connection;
                this.fromLocation = connection.fromLocation ?? null;
                this.toLocation = connection.toLocation ?? null;
                this.cables = connection.cables ?? [];
                this.noCableCores = connection.noCableCores ?? [];

                if (this.fromLocation) {
                    this.fromLocationOutPins = this.fromLocation.equipmentPins?.filter(pin => pin.pinDirection === PIN_DIRECTION.OUT) ?? [];
                    this.fromLocationOutCaps = this.fromLocation.equipmentCaps?.filter(cap => cap.capDirection === CAP_DIRECTION.OUT) ?? [];
                }
                if (this.toLocation) {
                    this.toLocationInPins = this.toLocation.equipmentPins?.filter(pin => pin.pinDirection === PIN_DIRECTION.IN) ?? [];
                    this.toLocationInCaps = this.toLocation.equipmentCaps?.filter(cap => cap.capDirection === CAP_DIRECTION.IN) ?? [];
                }

                this.initializeSelectedCaps();
                this.updateNodeDataArray();
                this.createLinksFromCoreConnections();
            })
            .catch((error: Error) => {
                console.error('Error fetching location connection:', error);
            });
    }

    /**
     * Creates links in the diagram based on existing core connections
     * This method reads equipmentInPinId and equipmentOutPinId from cores and creates corresponding links
     * It iterates over cables first and assigns indexed port IDs for each core (repeating every 5 cores)
     */
    private createLinksFromCoreConnections(): void {
        if (!this.myDiagram) return;

        const linkDataArray: any[] = [];

        // Process cables: iterate over each cable and assign indexed port IDs to its cores
        for (const cable of this.cables) {
            if (cable.cableCores && cable.cableCores.length > 0) {
                cable.cableCores.forEach((core, coreIndex) => {
                    // Calculate port index with modulo 5 to repeat after 5 cores
                    const portIndex = coreIndex % 5;

                    // Create link for equipmentInPinId (in-pin to core right side)
                    if (core.equipmentInPinId) {
                        linkDataArray.push({
                            from: core.equipmentInPinId,
                            to: core.id,
                            fromPort: `in-pin-left-side_${portIndex}`, // Use indexed port with modulo 5
                            toPort: 'core-right-side',
                        });
                    }

                    // Create link for equipmentOutPinId (core left side to out-pin)
                    if (core.equipmentOutPinId) {
                        linkDataArray.push({
                            from: core.id,
                            to: core.equipmentOutPinId,
                            fromPort: 'core-left-side',
                            toPort: `out-pin-right-side_${portIndex}`, // Use indexed port with modulo 5
                        });
                    }
                });
            }
        }

        // Process noCableCores: these are cores without a cable, assign incremental port IDs with modulo 5
        if (this.noCableCores && this.noCableCores.length > 0) {
            this.noCableCores.forEach((core, coreIndex) => {
                // Calculate port index with modulo 5 to repeat after 5 cores
                const portIndex = coreIndex % 5;

                // Create link for equipmentInPinId (in-pin to core right side)
                if (core.equipmentInPinId) {
                    linkDataArray.push({
                        from: core.equipmentInPinId,
                        to: core.id,
                        fromPort: `in-pin-left-side_${portIndex}`, // Use indexed port with modulo 5
                        toPort: 'core-right-side',
                    });
                }

                // Create link for equipmentOutPinId (core left side to out-pin)
                if (core.equipmentOutPinId) {
                    linkDataArray.push({
                        from: core.id,
                        to: core.equipmentOutPinId,
                        fromPort: 'core-left-side',
                        toPort: `out-pin-right-side_${portIndex}`, // Use indexed port with modulo 5
                    });
                }
            });
        }

        // Update the diagram model with the links
        if (linkDataArray.length > 0) {
            // const model = this.myDiagram.model as go.GraphLinksModel;
            // model.addLinkDataCollection(linkDataArray);
            this.myDiagram.model.startTransaction('links');
            (this.myDiagram.model as go.GraphLinksModel).linkDataArray = linkDataArray;
            this.myDiagram.model.commitTransaction('links');
        }

        console.log(`Created ${linkDataArray.length} links from existing core connections`);
    }

    private onDeleteCable(cable: CableModel): void {
        if (!this.hasCircuitEditPermission || this.readonly) return;

        this.ConfirmSrv.open('Delete Cable', `Are you sure you want to delete the cable "${cable.title}"?`).then(yes => {
            if (yes) {
                this.LoaderSrv.showFullScreenLoader();
                this.cablesApi
                    .deleteCableAsync(cable.id!)
                    .then(() => {
                        this.getLocations();
                        this.AlertSrv.show('success', 'Cable deleted successfully');
                    })
                    .catch(error => {
                        this.AlertSrv.show('error', `Failed to delete cable: ${error.message}`);
                    })
                    .finally(() => {
                        this.LoaderSrv.hideFullScreenLoader();
                    });
            }
        });
    }

    private onDeleteCore(core: CableCoreModel): void {
        if (!this.hasCircuitEditPermission || this.readonly) return;

        this.ConfirmSrv.open('Delete Core', `Are you sure you want to delete the core "${core.title || core.id}"?`).then(yes => {
            if (yes) {
                this.LoaderSrv.showFullScreenLoader();
                this.cableCoresApi
                    .deleteCableCore(core.id!)
                    .then(() => {
                        this.getLocations();
                        this.AlertSrv.show('success', 'Core deleted successfully');
                    })
                    .catch(error => {
                        this.AlertSrv.show('error', `Failed to delete core: ${error.message}`);
                    })
                    .finally(() => {
                        this.LoaderSrv.hideFullScreenLoader();
                    });
            }
        });
    }

    private initializeGoJsDiagram(): void {
        const me = this;
        this.myDiagram = $(go.Diagram, 'gojsConnectionPanel', {
            'draggingTool.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable dragging only with permission
            'undoManager.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable undo/redo only with permission
            'linkingTool.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable link drawing only with permission
            'relinkingTool.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable link editing only with permission
            'grid.visible': true,
            'grid.gridCellSize': new go.Size(40, 30),
            allowHorizontalScroll: true,
            allowVerticalScroll: true,
            // layout: $(go.GridLayout, {
            //     wrappingColumn: 3, // Two columns for Wire In and Wire Out
            //     alignment: go.GridLayout.Position,
            //     cellSize: new go.Size(1, 1),
            //     spacing: new go.Size(30, 10),
            // }),
        });

        this.myDiagram.nodeTemplateMap.add(
            'inPinsNodeTemplate',
            createConnectionPinsNodeTemplate(
                {
                    direction: 'IN',
                    readonly: !this.hasCircuitEditPermission || this.readonly,
                },
                {
                    onDeletePinClick: (pinId: string) => {
                        // Delete functionality can be implemented here if needed
                        console.log('Delete pin clicked:', pinId);
                    },
                    onPinClick: (pinId: string) => {
                        this.onPinClick(pinId);
                    },
                }
            )
        );

        this.myDiagram.nodeTemplateMap.add(
            'outPinsNodeTemplate',
            createConnectionPinsNodeTemplate(
                {
                    direction: 'OUT',
                    readonly: !this.hasCircuitEditPermission || this.readonly,
                },
                {
                    onDeletePinClick: (pinId: string) => {
                        // Delete functionality can be implemented here if needed
                        console.log('Delete pin clicked:', pinId);
                    },
                    onPinClick: (pinId: string) => {
                        this.onPinClick(pinId);
                    },
                }
            )
        );

        this.myDiagram.nodeTemplateMap.add(
            'coreNodeTemplate',
            $(
                go.Node,
                go.Panel.Horizontal,
                {
                    selectable: true,
                    movable: true,
                },
                $(
                    go.Panel,
                    go.Panel.Horizontal,
                    {
                        margin: new go.Margin(0, 0, 0, 0),
                        width: NaN,
                    },
                    // Left connection point
                    $(
                        go.Panel,
                        go.Panel.Vertical,
                        {
                            margin: 0,
                            width: NaN,
                        },
                        // Add border to each Picture by wrapping in a Panel with a Shape as border
                        $(
                            go.Panel,
                            go.Panel.Spot,
                            {},
                            $(go.Shape, 'Rectangle', {
                                desiredSize: new go.Size(24, 24),
                                stroke: '#1976d2',
                                strokeWidth: 1,
                                fill: 'transparent',
                                // geometryString: idx === 0 ? 'M0,0 L0,24 L24,24 L24,0 Z' : 'M0,0 L0,110 L200,110 L200,0',
                            }),
                            $(go.Picture, {
                                source: '/icons/link-variant.svg',
                                width: 20,
                                height: 20,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 0),
                                imageAlignment: go.Spot.Center,
                                imageStretch: go.GraphObject.Uniform,
                                scale: 1,
                                fromLinkable: false,
                                toLinkable: true,
                                toLinkableDuplicates: false,
                                toMaxLinks: 1,
                                portId: `core-left-side`,
                            })
                        )
                    ),
                    // Central area with core information and dotted line
                    $(
                        go.Panel,
                        go.Panel.Spot,
                        {
                            margin: 0,
                            width: 296,
                            alignment: go.Spot.Center,
                        },
                        // Dotted line from left to right
                        $(go.Shape, {
                            geometryString: 'M0,12 L246,12',
                            stroke: '#666',
                            strokeWidth: 2,
                            strokeDashArray: [5, 5],
                            position: new go.Point(0, 0),
                        }),
                        // Core information panel
                        $(
                            go.Panel,
                            go.Panel.Auto,
                            {
                                margin: 0,
                                alignment: go.Spot.Center,
                                click: (e, obj) => {
                                    const node = obj.part;
                                    const coreId = node?.data?.key;
                                    if (coreId) {
                                        me.onCoreClick(coreId);
                                    }
                                },
                            },
                            $(go.Shape, 'Rectangle', {
                                fill: '#f9f9f9',
                                stroke: '#ddd',
                                strokeWidth: 1,
                                parameter1: 5,
                            }),
                            $(
                                go.Panel,
                                go.Panel.Horizontal,
                                {
                                    margin: 0,
                                },
                                $(
                                    go.TextBlock,
                                    {
                                        margin: new go.Margin(0, 10, 0, 0),
                                        // font: 'bold 12pt sans-serif',
                                    },
                                    new go.Binding('text', 'text')
                                ),
                                $(go.Picture, {
                                    source: '/icons/delete-empty-red.svg',
                                    width: 16,
                                    height: 16,
                                    cursor: 'pointer',
                                    margin: new go.Margin(0, 0, 0, 0),
                                    imageAlignment: go.Spot.Center,
                                    imageStretch: go.GraphObject.Uniform,
                                    scale: 1,
                                    click: (e: any, obj: any) => {
                                        const node = obj.part;
                                        const coreId = node?.data?.key;
                                        if (coreId && me.hasCircuitEditPermission && !me.readonly) {
                                            // Find the core model and call delete method
                                            const allCores = [...me.cables.flatMap(cable => cable.cableCores || []), ...me.noCableCores];
                                            const core = allCores.find((c: CableCoreModel) => c.id === coreId);
                                            if (core) {
                                                me.onDeleteCore(core);
                                            }
                                        }
                                    },
                                })
                            )
                        )
                    ),
                    // Right connection point
                    $(
                        go.Panel,
                        go.Panel.Vertical,
                        {
                            margin: 0,
                            width: NaN,
                        },
                        // Add border to each Picture by wrapping in a Panel with a Shape as border
                        $(
                            go.Panel,
                            go.Panel.Spot,
                            {},
                            $(go.Shape, 'Rectangle', {
                                desiredSize: new go.Size(24, 24),
                                stroke: '#1976d2',
                                strokeWidth: 1,
                                fill: 'transparent',
                                // geometryString:
                                //     idx === 0
                                //         ? // All sides for first index
                                //           'M0,0 L0,24 L24,24 L24,0 Z'
                                //         : // Only left, right, bottom for others
                                //           'M0,0 L0,110 L200,110 L200,0',
                            }),
                            $(go.Picture, {
                                source: '/icons/link-variant.svg',
                                width: 20,
                                height: 20,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 0),
                                imageAlignment: go.Spot.Center,
                                imageStretch: go.GraphObject.Uniform,
                                scale: 1,
                                fromLinkable: true,
                                toLinkable: false,
                                toLinkableDuplicates: false,
                                toMaxLinks: 1,
                                fromSpot: go.Spot.Right,
                                portId: `core-right-side`,
                            })
                        )
                    )
                )
            )
        );

        this.myDiagram.groupTemplateMap.add(
            'cableNodeGroupTemplate',
            $(
                go.Group,
                go.Panel.Vertical,
                {
                    selectable: true,
                    movable: true,
                    layout: $(go.GridLayout, {
                        wrappingColumn: 1,
                        alignment: go.GridLayout.Position,
                        cellSize: new go.Size(1, 1),
                        spacing: new go.Size(0, 2),
                        comparer: (a: go.Part, b: go.Part) => {
                            const aOrder = a.data.sortOrder ?? 0;
                            const bOrder = b.data.sortOrder ?? 0;
                            return aOrder - bOrder;
                        },
                    }),
                    isSubGraphExpanded: true,
                    computesBoundsAfterDrag: true,
                    handlesDragDropForMembers: true,
                    mouseDragEnter: (e, group: any) => {
                        group.isHighlighted = true;
                    },
                    mouseDragLeave: (e, group: any) => {
                        group.isHighlighted = false;
                    },
                    mouseDrop: (e, group: any) => {
                        if (!me.hasCircuitEditPermission || me.readonly) return;
                        const tool = e.diagram.toolManager.draggingTool;
                        const draggedParts = tool.draggedParts;
                        if (draggedParts) {
                            draggedParts.each(part => {
                                if (part instanceof go.Node) {
                                    part.containingGroup = group as any;
                                }
                            });
                        }
                        group.isHighlighted = false;
                        group.layout.invalidateLayout();

                        // Sort nodeDataArray data of the group based on sortOrder
                        const groupKey = group.data.key;
                        const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

                        groupNodes.sort((a: any, b: any) => {
                            const nodeA = me.myDiagram!.findNodeForData(a);
                            const nodeB = me.myDiagram!.findNodeForData(b);
                            return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
                        });

                        groupNodes.forEach((node: any, index: number) => {
                            e.diagram.model.setDataProperty(node, 'sortOrder', index);
                        });
                    },
                },
                // Cable header with title and controls
                $(
                    go.Panel,
                    go.Panel.Auto,
                    {
                        margin: 0,
                    },
                    $(go.Shape, 'Rectangle', {
                        fill: '#f5f5f5',
                        stroke: '#cccccc',
                        strokeWidth: 1,
                        parameter1: 15,
                        width: 348,
                    }),
                    $(
                        go.Panel,
                        go.Panel.Vertical,
                        {},
                        $(
                            go.Panel,
                            go.Panel.Horizontal,
                            {
                                margin: 0,
                            },
                            $(
                                go.Panel,
                                go.Panel.Auto,
                                {
                                    margin: new go.Margin(0, 7, 0, 1),
                                    cursor: 'pointer',
                                    click: (e: any, obj: any) => {
                                        const group = obj.part;
                                        const cableId = group?.data?.key;
                                        if (cableId && me.hasCircuitEditPermission && !me.readonly) {
                                            me.openSelectCapDialog(cableId, 'out');
                                        }
                                    },
                                },
                                $(go.Shape, 'Rectangle', {
                                    fill: '#f5f5f5',
                                    stroke: '#000',
                                    strokeWidth: 1,
                                    parameter1: 15,
                                }),
                                $(
                                    go.TextBlock,
                                    {
                                        margin: new go.Margin(0, 0, 0, 0),
                                        stroke: '#1976d2',
                                    },
                                    new go.Binding('text', 'key', cableId => {
                                        return 'Select Out Cap';
                                    })
                                )
                            ),
                            $(
                                go.TextBlock,
                                {
                                    margin: new go.Margin(0, 10, 0, 0),
                                    font: 'bold 12pt sans-serif',
                                    cursor: 'pointer',
                                    click: (e: any, obj: any) => {
                                        // Prevent click event from propagating to the node
                                        // e.stopPropagation();
                                        const node = obj.part;
                                        const cableId = node?.data?.key;
                                        if (cableId) {
                                            me.onCableClick(cableId);
                                        } else {
                                            me.selectedCable = null;
                                        }
                                    },
                                },
                                new go.Binding('text', 'text')
                            ),
                            $(
                                go.Shape,
                                'Circle',
                                {
                                    desiredSize: new go.Size(14, 14),
                                    stroke: 'black',
                                    fill: 'white',
                                    strokeWidth: 2,
                                    cursor: 'pointer',
                                    margin: new go.Margin(0, 0, 0, 0),
                                },
                                new go.Binding('fill', 'isActive', function (s) {
                                    return s ? 'black' : 'white';
                                })
                            ),
                            $(go.Picture, {
                                source: '/icons/delete-empty-red.svg',
                                width: 20,
                                height: 20,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 10),
                                imageAlignment: go.Spot.Center,
                                imageStretch: go.GraphObject.Uniform,
                                scale: 1,
                                click: (e: any, obj: any) => {
                                    const group = obj.part;
                                    const cableId = group?.data?.key;
                                    if (cableId) {
                                        const cable = me.cables.find(c => c.id === cableId);
                                        if (cable) {
                                            me.onDeleteCable(cable);
                                        }
                                    }
                                },
                            }),
                            $(
                                go.Panel,
                                go.Panel.Auto,
                                {
                                    margin: new go.Margin(0, 0, 0, 7),
                                    cursor: 'pointer',
                                    click: (e: any, obj: any) => {
                                        const group = obj.part;
                                        const cableId = group?.data?.key;
                                        if (cableId && me.hasCircuitEditPermission && !me.readonly) {
                                            me.openSelectCapDialog(cableId, 'in');
                                        }
                                    },
                                },
                                $(go.Shape, 'Rectangle', {
                                    fill: '#f5f5f5',
                                    stroke: '#000',
                                    strokeWidth: 1,
                                    parameter1: 15,
                                }),
                                $(
                                    go.TextBlock,
                                    {
                                        margin: new go.Margin(0, 0, 0, 0),
                                        stroke: '#1976d2',
                                    },
                                    new go.Binding('text', 'key', cableId => {
                                        return 'Select In Cap';
                                    })
                                )
                            )
                        ),
                        // Placeholder for child nodes (cores)
                        $(
                            go.Panel,
                            go.Panel.Auto,
                            {
                                margin: 0,
                            },
                            new go.Placeholder({padding: 1}) // Add padding inside the rectangle
                        ),
                        // Display selected caps
                        $(
                            go.Panel,
                            go.Panel.Horizontal,
                            {
                                margin: new go.Margin(5, 0, 0, 0),
                            },
                            $(
                                go.Panel,
                                go.Panel.Auto,
                                {
                                    margin: new go.Margin(0, 5, 0, 0),
                                },
                                $(go.Shape, 'RoundedRectangle', {
                                    fill: '#e8f5e8',
                                    stroke: '#4caf50',
                                    strokeWidth: 1,
                                    parameter1: 5,
                                }),
                                $(
                                    go.TextBlock,
                                    {
                                        margin: new go.Margin(2, 5, 2, 5),
                                        font: '10pt sans-serif',
                                        stroke: '#2e7d32',
                                    },
                                    new go.Binding('text', 'selectedOutCapText', selectedOutCap => {
                                        return selectedOutCap !== 'Select Out Cap' ? `Out: ${selectedOutCap}` : '';
                                    }),
                                    new go.Binding('visible', 'selectedOutCapText', selectedOutCap => {
                                        return selectedOutCap !== 'Select Out Cap';
                                    })
                                )
                            ),
                            $(
                                go.Panel,
                                go.Panel.Auto,
                                {
                                    margin: new go.Margin(0, 0, 0, 5),
                                },
                                $(go.Shape, 'RoundedRectangle', {
                                    fill: '#e3f2fd',
                                    stroke: '#2196f3',
                                    strokeWidth: 1,
                                    parameter1: 5,
                                }),
                                $(
                                    go.TextBlock,
                                    {
                                        margin: new go.Margin(2, 5, 2, 5),
                                        font: '10pt sans-serif',
                                        stroke: '#1565c0',
                                    },
                                    new go.Binding('text', 'selectedInCapText', selectedInCap => {
                                        return selectedInCap !== 'Select In Cap' ? `In: ${selectedInCap}` : '';
                                    }),
                                    new go.Binding('visible', 'selectedInCapText', selectedInCap => {
                                        return selectedInCap !== 'Select In Cap';
                                    })
                                )
                            )
                        )
                    )
                )
            )
        );

        const afterNodeAddButtonTemplate = $(
            go.Node,
            go.Panel.Horizontal,
            {
                margin: 0,
                selectable: false,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    margin: 0,
                },
                $(go.Shape, 'RoundedRectangle', {
                    fill: '#f5f5f5',
                    stroke: '#cccccc',
                    strokeWidth: 2,
                    parameter1: 15,
                    height: 20,
                    width: 20,
                }),
                $(
                    go.Panel,
                    go.Panel.Horizontal,
                    {
                        margin: 0,
                    },
                    $(go.Picture, {
                        source: '/icons/gridicons--add.svg',
                        width: 18,
                        height: 18,
                        cursor: 'pointer',
                        margin: new go.Margin(0, 0, 0, 0),
                        imageAlignment: go.Spot.Center,
                        imageStretch: go.GraphObject.Uniform,
                        scale: 1,
                        click: (e: any, obj: any) => {
                            const node = obj.part;
                            if (node) {
                                const data = node.data;
                                if (data.type === 'afterCable') {
                                    const cableId = data.cableId;
                                    if (!!cableId && cableId !== 'no-cable') {
                                        const cable = this.cables.find(c => c.id === cableId);
                                        if (cable) {
                                            this.addSingleCable(cable, data.where);
                                        }
                                    } else {
                                        this.addSingleCable({sortOrder: 1} as CableModel, data.where);
                                    }
                                } else if (data.type === 'afterCore') {
                                    const cableId = data.cableId;
                                    if (!!cableId && cableId !== 'no-cable') {
                                        const cable = this.cables.find(c => c.id === cableId);
                                        if (cable) {
                                            this.addSingleCableCore(cable);
                                        }
                                    } else if (cableId === 'no-cable') {
                                        this.addSingleCoreWithoutCable();
                                    }
                                }
                            }
                            console.log('Add button clicked', node.data);
                        },
                    })
                )
            )
        );

        this.myDiagram.nodeTemplateMap.add('afterNodeAddButtonTemplate', afterNodeAddButtonTemplate);

        this.myDiagram.linkTemplate = new go.Link({
            curve: go.Link.Bezier,
            layerName: 'Foreground',
        }).add(new go.Shape({strokeWidth: 3}));

        // Add link validation to prevent cores from connecting to other cores or themselves
        this.myDiagram.toolManager.linkingTool.linkValidation = (fromNode: go.Node, fromPort: go.GraphObject, toNode: go.Node, toPort: go.GraphObject) => {
            return me.validateLink(fromNode, fromPort, toNode, toPort);
        };

        // Also apply validation to the relinking tool
        this.myDiagram.toolManager.relinkingTool.linkValidation = (fromNode: go.Node, fromPort: go.GraphObject, toNode: go.Node, toPort: go.GraphObject) => {
            return me.validateLink(fromNode, fromPort, toNode, toPort);
        };

        const inCapsGroupTemplate = this.createGroupTemplate('IN_CAPS');
        const inPinsGroupTemplate = this.createGroupTemplate('IN_PINS');

        const outCapsGroupTemplate = this.createGroupTemplate('OUT_CAPS');
        const outPinsGroupTemplate = this.createGroupTemplate('OUT_PINS');

        this.myDiagram.groupTemplateMap.add('IN_CAPS', inCapsGroupTemplate);
        this.myDiagram.groupTemplateMap.add('IN_PINS', inPinsGroupTemplate);

        this.myDiagram.groupTemplateMap.add('OUT_CAPS', outCapsGroupTemplate);
        this.myDiagram.groupTemplateMap.add('OUT_PINS', outPinsGroupTemplate);

        this.myDiagram.groupTemplateMap.add('CABLES', inCapsGroupTemplate);
        this.myDiagram.groupTemplateMap.add('CORES', inCapsGroupTemplate);

        const gridLayout = new go.GridLayout();
        gridLayout.wrappingColumn = 5;
        gridLayout.wrappingWidth = 1500;
        gridLayout.isViewportSized = false;
        gridLayout.alignment = go.GridLayout.Position;
        gridLayout.cellSize = new go.Size(1, 1);
        gridLayout.spacing = new go.Size(110, 0);

        this.myDiagram.layout = gridLayout;

        const outerBoxGroupTemplate = $(
            go.Group,
            go.Panel.Auto,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1, // Two columns for Cap In, Pin In and Pin Out, Cap Out
                    alignment: go.GridLayout.Position,
                    cellSize: new go.Size(1, 1),
                    spacing: new go.Size(0, 1),
                }),
                // width: 1010,
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(200, 200, 200, 0.33)',
                    margin: 0,
                    stroke: '#cccccc',
                    strokeWidth: 1,
                }),
                new go.Placeholder({padding: 0}) // Add padding inside the rectangle
            )
        );

        const rowOneGroupTemplate = $(
            go.Group,
            go.Panel.Horizontal,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 5,
                    alignment: go.GridLayout.Position,
                    spacing: new go.Size(0, 0),
                    cellSize: new go.Size(1, 1),
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(229, 190, 190, 0.33)',
                    margin: 0,
                    stroke: '#000',
                    strokeWidth: 0,
                    // width: 855,
                    // height: 200,
                }),
                new go.Placeholder({padding: 0})
            )
        );

        const rowTwoGroupTemplate = $(
            go.Group,
            go.Panel.Horizontal,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 5, // Two columns for Cap In, Pin In and Pin Out, Cap Out
                    alignment: go.GridLayout.Position,
                    spacing: new go.Size(0, 0),
                    cellSize: new go.Size(1, 1),
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(162, 205, 171, 0.33)',
                    margin: 0,
                    stroke: '#000',
                    strokeWidth: 0,
                    // width: 816,
                    height: 100,
                }),
                new go.Placeholder({padding: 0})
            )
        );

        const colOneGroupTemplate = $(
            go.Group,
            go.Panel.Vertical,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1,
                    alignment: go.GridLayout.Position,
                    spacing: new go.Size(0, 0),
                    cellSize: new go.Size(1, 1),
                    comparer: (a: go.Part, b: go.Part) => {
                        const aOrder = a.data.sortOrder ?? 0;
                        const bOrder = b.data.sortOrder ?? 0;
                        return aOrder - bOrder;
                    },
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    // alignment: go.Spot.Top,
                },
                $(
                    go.Panel,
                    go.Panel.Auto,
                    {
                        alignment: go.Spot.Top,
                    },
                    $(go.Shape, 'Rectangle', {
                        fill: 'rgba(162, 205, 171, 0.33)',
                        margin: 0,
                        stroke: '#000',
                        strokeWidth: 1,
                        width: 162, // <-- Set your desired width for colOne
                        height: 540,
                    }),
                    new go.Placeholder({padding: 0, alignment: go.Spot.Top})
                )
            )
        );

        const colThreeGroupTemplate = $(
            go.Group,
            go.Panel.Vertical,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1,
                    alignment: go.GridLayout.Position,
                    spacing: new go.Size(0, 0),
                    cellSize: new go.Size(1, 1),
                    comparer: (a: go.Part, b: go.Part) => {
                        const aOrder = a.data.sortOrder ?? 0;
                        const bOrder = b.data.sortOrder ?? 0;
                        return aOrder - bOrder;
                    },
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
                mouseDragEnter: (e, group: any) => {
                    group.isHighlighted = true;
                },
                mouseDragLeave: (e, group: any) => {
                    group.isHighlighted = false;
                },
                mouseDrop: (e, group: any) => {
                    if (!me.hasCircuitEditPermission || me.readonly) return;
                    const tool = e.diagram.toolManager.draggingTool;
                    const draggedParts = tool.draggedParts;
                    if (draggedParts) {
                        draggedParts.each(part => {
                            if (part instanceof go.Node) {
                                part.containingGroup = group as any;
                            }
                        });
                    }
                    group.isHighlighted = false;
                    group.layout.invalidateLayout(); // Trigger layout rearrangement

                    // Sort nodeDataArray data of the group based on sortOrder
                    const groupKey = group.data.key;
                    const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

                    groupNodes.sort((a: any, b: any) => {
                        const nodeA = me.myDiagram!.findNodeForData(a);
                        const nodeB = me.myDiagram!.findNodeForData(b);
                        return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
                    });

                    groupNodes.forEach((node: any, index: number) => {
                        // node.sortOrder = index; // Update sortOrder locally
                        e.diagram.model.setDataProperty(node, 'sortOrder', index);
                    });
                },
            },
            $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    // alignment: go.Spot.Top,
                },
                $(
                    go.Panel,
                    go.Panel.Auto,
                    {
                        alignment: go.Spot.Top,
                    },
                    $(go.Shape, 'Rectangle', {
                        fill: 'rgba(162, 205, 171, 0.33)',
                        margin: 0,
                        stroke: '#000',
                        strokeWidth: 1,
                        width: 350, // <-- Set your desired width for colOne
                        // height: 540,
                    }),
                    new go.Placeholder({padding: 0, alignment: go.Spot.Top})
                )
            )
        );

        const colRow2GroupTemplate = $(
            go.Group,
            go.Panel.Horizontal,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1, // Two columns for Cap In, Pin In and Pin Out, Cap Out
                    alignment: go.GridLayout.Position,
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    alignment: go.Spot.Top,
                },
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(162, 205, 171, 0.33)',
                    margin: 0,
                    stroke: '#000',
                    strokeWidth: 1,
                    width: 162,
                    height: 100,
                }),
                new go.Placeholder({padding: 5, alignment: go.Spot.Top})
            )
        );

        const col3Row2GroupTemplate = $(
            go.Group,
            go.Panel.Horizontal,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1, // Two columns for Cap In, Pin In and Pin Out, Cap Out
                    alignment: go.GridLayout.Position,
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    alignment: go.Spot.Top,
                },
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(162, 205, 171, 0.33)',
                    margin: 0,
                    stroke: '#000',
                    strokeWidth: 1,
                    width: 300,
                    height: 100,
                }),
                new go.Placeholder({padding: 5, alignment: go.Spot.Top})
            )
        );

        this.myDiagram.groupTemplateMap.add('OUT_BOX', outerBoxGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_ONE', rowOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_TWO', rowTwoGroupTemplate);

        this.myDiagram.groupTemplateMap.add('ROW_1_COL_ONE', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_TWO', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_THREE', colThreeGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_FOUR', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_FIVE', colOneGroupTemplate);

        this.myDiagram.groupTemplateMap.add('ROW_2_COL_ONE', colRow2GroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_2_COL_TWO', colRow2GroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_2_COL_THREE', col3Row2GroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_2_COL_FOUR', colRow2GroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_2_COL_FIVE', colRow2GroupTemplate);

        this.myDiagram.model = new go.GraphLinksModel({
            linkFromPortIdProperty: 'fromPort',
            linkToPortIdProperty: 'toPort',
            nodeDataArray: [
                {key: 'out-box', text: 'Outer Box', isGroup: true, category: 'OUT_BOX'},
                {key: 'row-one', text: 'Row One', isGroup: true, category: 'ROW_ONE', group: 'out-box'},
                {key: 'row-two', text: 'Row Two', isGroup: true, category: 'ROW_TWO', group: 'out-box'},

                {key: 'col-one', text: 'Out Pins', isGroup: true, category: 'ROW_1_COL_ONE', group: 'row-one'},
                {key: 'col-two', text: '', isGroup: true, category: 'ROW_1_COL_TWO', group: 'row-one'},
                {key: 'col-three', text: 'Cables', isGroup: true, category: 'ROW_1_COL_THREE', group: 'row-one'},
                {key: 'col-four', text: '', isGroup: true, category: 'ROW_1_COL_FOUR', group: 'row-one'},
                {key: 'col-five', text: 'IN Pins', isGroup: true, category: 'ROW_1_COL_FIVE', group: 'row-one'},

                {key: 'row-2-col-one', text: 'Col One', isGroup: true, category: 'ROW_2_COL_ONE', group: 'row-two'},
                {key: 'row-2-col-two', text: 'Col Two', isGroup: true, category: 'ROW_2_COL_TWO', group: 'row-two'},
                {key: 'row-2-col-three', text: 'Col Three', isGroup: true, category: 'ROW_2_COL_THREE', group: 'row-two'},
                {key: 'row-2-col-four', text: 'Col Four', isGroup: true, category: 'ROW_2_COL_FOUR', group: 'row-two'},
                {key: 'row-2-col-five', text: 'Col Five', isGroup: true, category: 'ROW_2_COL_FIVE', group: 'row-two'},

                // {key: 'WIRE_OUT', text: 'Equipment Out', isGroup: true, category: 'WIRE_OUT'},
                // {key: 'CABLES_SECTION', text: 'Cables Section', isGroup: true, category: 'CABLES_SECTION'},
                // {key: 'WIRE_IN', text: 'Equipment In', isGroup: true, category: 'WIRE_IN'},

                // {key: 'OUT_PINS', text: 'OUT Pins', isGroup: true, category: 'OUT_PINS', group: 'WIRE_OUT'},
                // // {key: 'OUT_CAPS', text: 'OUT Caps', isGroup: true, category: 'OUT_CAPS', group: 'WIRE_OUT'},

                // // {key: 'CABLES_LEFT', text: 'Cables', isGroup: true, category: 'CABLES', group: 'CABLES_SECTION'},
                // {key: 'CORES', text: 'Cores', isGroup: true, category: 'CORES', group: 'CABLES_SECTION'},
                // // {key: 'CABLES_RIGHT', text: 'Cables', isGroup: true, category: 'CABLES', group: 'CABLES_SECTION'},

                // // {key: 'IN_CAPS', text: 'IN Caps', isGroup: true, category: 'IN_CAPS', group: 'WIRE_IN'},
                // {key: 'IN_PINS', text: 'IN Pins', isGroup: true, category: 'IN_PINS', group: 'WIRE_IN'},
            ],
            linkDataArray: [],
        });

        this.myDiagram.addDiagramListener('LinkDrawn', e => {
            const link = e.subject as go.Link;
            const fromNode = link.fromNode as go.Node;
            const toNode = link.toNode as go.Node;

            if (!fromNode || !toNode) return;

            // Handle core to pin connections
            this.handleCorePinConnection(fromNode, toNode, link);
        });

        this.myDiagram.addDiagramListener('SelectionDeleted', e => {
            const deletedParts = e.subject as go.Set<go.Part>;
            deletedParts.each(part => {
                if (part instanceof go.Link) {
                    this.handleCorePinDisconnection(part);
                }
            });
        });

        this.updateNodeDataArray();
    }

    /**
     * Validates if a link can be created between two nodes
     * Prevents cores from connecting to other cores or themselves
     */
    private validateLink(fromNode: go.Node, fromPort: go.GraphObject, toNode: go.Node, toPort: go.GraphObject): boolean {
        // Prevent linking to itself
        if (fromNode === toNode) {
            return false;
        }

        // Get the node data to determine the node types
        const fromNodeData = fromNode.data;
        const toNodeData = toNode.data;

        // Check if both nodes are core nodes
        const isFromNodeCore = fromNodeData && fromNodeData.category === 'coreNodeTemplate';
        const isToNodeCore = toNodeData && toNodeData.category === 'coreNodeTemplate';

        // Prevent core-to-core connections
        if (isFromNodeCore && isToNodeCore) {
            return false;
        }

        // Additional validation: Check if the connection would create a duplicate link
        const existingLinks = this.myDiagram?.links;
        if (existingLinks) {
            const isDuplicate = existingLinks.any((link: go.Link) => {
                return link.fromNode === fromNode && link.fromPortId === fromPort.portId && link.toNode === toNode && link.toPortId === toPort.portId;
            });

            if (isDuplicate) {
                return false;
            }
        }

        // Allow all other connections (core to pin, pin to core, pin to pin, etc.)
        return true;
    }

    /**
     * Handles core to pin connections when a link is drawn
     * Updates the core's equipmentInPinId or equipmentOutPinId based on the connection
     */
    private async handleCorePinConnection(fromNode: go.Node, toNode: go.Node, link: go.Link): Promise<void> {
        const fromData = fromNode.data;
        const toData = toNode.data;

        let coreNode: go.Node | null = null;
        let pinNode: go.Node | null = null;
        let isFromCore = false;

        // Determine which node is the core and which is the pin
        if (fromData?.category === 'coreNodeTemplate' && (toData?.type === 'pin-in' || toData?.type === 'pin-out')) {
            coreNode = fromNode;
            pinNode = toNode;
            isFromCore = true;
        } else if (toData?.category === 'coreNodeTemplate' && (fromData?.type === 'pin-in' || fromData?.type === 'pin-out')) {
            coreNode = toNode;
            pinNode = fromNode;
            isFromCore = false;
        }

        // If this is not a core-pin connection, return early
        if (!coreNode || !pinNode) {
            return;
        }

        const coreId = coreNode.data.key;
        const pinId = pinNode.data.key;
        const pinType = pinNode.data.type;

        // Find the core model from our data
        const allCores = [...this.cables.flatMap(cable => cable.cableCores || []), ...this.noCableCores];
        const coreModel = allCores.find((core: CableCoreModel) => core.id === coreId);

        if (!coreModel) {
            console.error('Core model not found for ID:', coreId);
            return;
        }

        // Check if this connection is a duplicate (core is already connected to this pin)
        const existingPinId = pinType === 'pin-in' ? coreModel.equipmentInPinId : coreModel.equipmentOutPinId;

        if (existingPinId === pinId) {
            // This is a duplicate connection - remove the current link and return
            console.log(`Duplicate connection detected: Core ${coreId} is already connected to ${pinType} ${pinId}`);
            this.myDiagram?.remove(link);
            return;
        }

        try {
            // Create update payload
            const updateData = new CreateEditCableCoreViewModel({
                title: coreModel.title,
                cableId: coreModel.cableId,
                equipmentInPinId: coreModel.equipmentInPinId,
                equipmentOutPinId: coreModel.equipmentOutPinId,
                locationConnectionId: coreModel.locationConnectionId,
            });

            // Update the appropriate pin ID based on pin type
            if (pinType === 'pin-in') {
                updateData.equipmentInPinId = pinId;
            } else if (pinType === 'pin-out') {
                updateData.equipmentOutPinId = pinId;
            }

            // Show loading
            this.LoaderSrv.showFullScreenLoader();

            // Call API to update the core
            await this.cableCoresApi.updateCableCore(coreId, updateData);

            // Update local model
            if (pinType === 'pin-in') {
                coreModel.equipmentInPinId = pinId;
            } else if (pinType === 'pin-out') {
                coreModel.equipmentOutPinId = pinId;
            }

            console.log(`Core ${coreId} updated with ${pinType} connection to pin ${pinId}`);

            // Reload data to update links based on backend changes
            await this.getLocations();
        } catch (error) {
            console.error('Failed to update core connection:', error);
            this.AlertSrv?.show('error', 'Failed to save connection. Please try again.');

            // Remove the link since the update failed
            if (this.myDiagram) {
                this.myDiagram.remove(link);
            }
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    /**
     * Handles core to pin disconnections when a link is deleted
     */
    private async handleCorePinDisconnection(link: go.Link): Promise<void> {
        const fromNode = link.fromNode as go.Node;
        const toNode = link.toNode as go.Node;

        if (!fromNode || !toNode) return;

        const fromData = fromNode.data;
        const toData = toNode.data;

        let coreNode: go.Node | null = null;
        let pinNode: go.Node | null = null;

        // Determine which node is the core and which is the pin
        if (fromData?.category === 'coreNodeTemplate' && (toData?.type === 'pin-in' || toData?.type === 'pin-out')) {
            coreNode = fromNode;
            pinNode = toNode;
        } else if (toData?.category === 'coreNodeTemplate' && (fromData?.type === 'pin-in' || fromData?.type === 'pin-out')) {
            coreNode = toNode;
            pinNode = fromNode;
        }

        if (!coreNode || !pinNode) return;

        const coreId = coreNode.data.key;
        const pinId = pinNode.data.key;
        const pinType = pinNode.data.type;

        try {
            const allCores = [...this.cables.flatMap(cable => cable.cableCores || []), ...this.noCableCores];
            const coreModel = allCores.find((core: CableCoreModel) => core.id === coreId);

            if (!coreModel) return;

            const updateData = new CreateEditCableCoreViewModel({
                title: coreModel.title,
                cableId: coreModel.cableId,
                equipmentInPinId: coreModel.equipmentInPinId,
                equipmentOutPinId: coreModel.equipmentOutPinId,
                locationConnectionId: coreModel.locationConnectionId,
            });

            // Clear the appropriate pin ID by setting it to null
            if (pinType === 'pin-in') {
                updateData.equipmentInPinId = null;
            } else if (pinType === 'pin-out') {
                updateData.equipmentOutPinId = null;
            }

            this.LoaderSrv.showFullScreenLoader();
            await this.cableCoresApi.updateCableCore(coreId, updateData);

            // Update local model
            if (pinType === 'pin-in') {
                coreModel.equipmentInPinId = null;
            } else if (pinType === 'pin-out') {
                coreModel.equipmentOutPinId = null;
            }

            console.log(`Core ${coreId} disconnected from ${pinType} ${pinId} (set to null)`);

            // Reload data to update links based on backend changes
            await this.getLocations();
        } catch (error) {
            console.error('Failed to update core disconnection:', error);
            this.AlertSrv?.show('error', 'Failed to save disconnection. Please try again.');
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    private updateNodeDataArray(): void {
        const nodeDataArray: any[] = [
            {key: 'no-cable', text: 'No Cable', group: 'col-three', category: 'cableNodeGroupTemplate', sortOrder: 1, isGroup: true},
            // Sample core nodes for testing
            // {key: 'core-1-no', text: 'Core 1', group: 'no-cable', category: 'coreNodeTemplate', sortOrder: 1},
            // {key: 'core-2-no', text: 'Core 2', group: 'no-cable', category: 'coreNodeTemplate', sortOrder: 2},
            // {key: 'cable-1', text: 'Cable 1', group: 'col-three', category: 'cableNodeGroupTemplate', sortOrder: 2},
            // {key: 'cable-2', text: 'Cable 2', group: 'col-three', category: 'cableNodeGroupTemplate', sortOrder: 3},
            // {key: 'cable-1-r', text: 'Cable 1', group: 'CABLES_RIGHT', category: 'cableNodeGroupTemplate', sortOrder: 1},
            // {key: 'core-1', text: 'Core 1', group: 'cable-1', category: 'coreNodeTemplate', sortOrder: 1},
            // {key: 'core-2', text: 'Core 2', group: 'cable-1', category: 'coreNodeTemplate', sortOrder: 2},
            // {key: 'core-3', text: 'Core 3', group: 'cable-1', category: 'coreNodeTemplate', sortOrder: 3},
            // {key: 'core-4', text: 'Core 1', group: 'cable-2', category: 'coreNodeTemplate', sortOrder: 1},
            // {key: 'core-5', text: 'Core 2', group: 'cable-2', category: 'coreNodeTemplate', sortOrder: 2},
            // {key: 'core-6', text: 'Core 3', group: 'cable-2', category: 'coreNodeTemplate', sortOrder: 3},
        ];

        // for (const cap of [this.fromLocationOutCaps, this.toLocationInCaps].flat()) {
        //     nodeDataArray.push({
        //         key: cap.id,
        //         text: cap.title,
        //         group: cap.capDirection === CAP_DIRECTION.IN ? 'IN_CAPS' : 'OUT_CAPS',
        //         category: cap.capDirection === CAP_DIRECTION.IN ? 'inCapsNodeTemplate' : 'outCapsNodeTemplate',
        //         sortOrder: cap.sortOrder ?? 0,
        //         isActive: cap.isActive,
        //         type: cap.capDirection === CAP_DIRECTION.IN ? 'cap-in' : 'cap-out',
        //     });
        // }

        for (const pin of [this.fromLocationOutPins, this.toLocationInPins].flat()) {
            nodeDataArray.push({
                key: pin.id,
                text: pin.title,
                group: pin.pinDirection === PIN_DIRECTION.IN ? 'col-five' : 'col-one',
                category: pin.pinDirection === PIN_DIRECTION.IN ? 'inPinsNodeTemplate' : 'outPinsNodeTemplate',
                sortOrder: pin.sortOrder ?? 0,
                isActive: pin.isActive,
                type: pin.pinDirection === PIN_DIRECTION.IN ? 'pin-in' : 'pin-out',
            });
        }

        // const linksDataArray: any[] = [];

        // for (const pin of [this.fromLocationOutPins, this.toLocationInPins].flat()) {
        //     if (pin.equipmentCapId) {
        //         linksDataArray.push({
        //             to: pin.id,
        //             from: pin.equipmentCapId,
        //             toPort: pin.pinDirection === PIN_DIRECTION.IN ? 'in-pin-left-side' : 'out-pin-right-side',
        //             fromPort: pin.pinDirection === PIN_DIRECTION.IN ? 'in-cap-right-side' : 'out-cap-left-side',
        //         });
        //     }

        //     if (pin.connectedPinId) {
        //         const isLinked = linksDataArray.some(link => {
        //             return (link.from === pin.id && link.to === pin.connectedPinId) || (link.from === pin.connectedPinId && link.to === pin.id);
        //         });
        //         if (isLinked) continue;

        //         linksDataArray.push({
        //             from: pin.id,
        //             to: pin.connectedPinId,
        //             fromPort: pin.pinDirection === PIN_DIRECTION.IN ? 'in-pin-right-side' : 'out-pin-left-side',
        //             toPort: pin.pinDirection === PIN_DIRECTION.IN ? 'in-pin-left-side' : 'out-pin-right-side',
        //         });
        //     }
        // }

        for (const cable of this.cables) {
            nodeDataArray.push({
                key: cable.id,
                text: cable.title,
                group: 'col-three',
                category: 'cableNodeGroupTemplate',
                sortOrder: cable.sortOrder ?? 0,
                isActive: cable.isActive,
                type: '',
                isGroup: true,
                selectedInCapText: this.getSelectedCapText(cable.id || '', 'in'),
                selectedOutCapText: this.getSelectedCapText(cable.id || '', 'out'),
            });

            if (cable.cableCores?.length) {
                for (const core of cable.cableCores) {
                    nodeDataArray.push({
                        key: core.id,
                        text: core.title,
                        group: cable.id,
                        category: 'coreNodeTemplate',
                        sortOrder: core.sortOrder ?? 0,
                        isActive: core.isActive,
                    });
                }
            }
        }

        for (const noCableCore of this.noCableCores) {
            nodeDataArray.push({
                key: noCableCore.id,
                text: noCableCore.title,
                group: 'no-cable',
                category: 'coreNodeTemplate',
                sortOrder: noCableCore.sortOrder ?? 0,
                isActive: noCableCore.isActive,
            });
        }

        if (this.myDiagram) {
            const removedNodes = this.myDiagram.model.nodeDataArray.filter(node => {
                return (
                    !nodeDataArray.some(n => n.key === node.key) &&
                    (!node.isGroup || (node.isGroup && (node.group === 'CABLES_SECTION' || node.category === 'cableNodeGroupTemplate')))
                );
            });
            const newAddedNodes = nodeDataArray.filter(node => {
                return (
                    (!node.isGroup || (node.isGroup && (node.group === 'CABLES_SECTION' || node.category === 'cableNodeGroupTemplate'))) &&
                    !this.myDiagram!.model.nodeDataArray.some(n => n.key === node.key)
                );
            });

            const nodesToUpdate = this.myDiagram.model.nodeDataArray.filter(node => {
                return (
                    nodeDataArray.some(n => n.key === node.key) &&
                    (!node.isGroup || (node.isGroup && (node.group === 'CABLES_SECTION' || node.category === 'cableNodeGroupTemplate')))
                );
            });

            for (const node of removedNodes) {
                this.myDiagram.model.removeNodeData(node);
            }

            for (const node of nodesToUpdate) {
                const newNode = nodeDataArray.find(n => n.key === node.key);
                if (newNode) {
                    this.myDiagram?.model.commit(tx => {
                        this.myDiagram?.model.setDataProperty(node, 'text', newNode.text);
                        this.myDiagram?.model.setDataProperty(node, 'sortOrder', newNode.sortOrder);
                        this.myDiagram?.model.setDataProperty(node, 'isActive', newNode.isActive);

                        // Update cap text properties for cable nodes
                        if (newNode.category === 'cableNodeGroupTemplate') {
                            this.myDiagram?.model.setDataProperty(node, 'selectedInCapText', newNode.selectedInCapText);
                            this.myDiagram?.model.setDataProperty(node, 'selectedOutCapText', newNode.selectedOutCapText);
                        }
                    });
                }
            }

            this.myDiagram?.model.commit(tx => {
                for (const node of newAddedNodes) {
                    this.myDiagram!.model.addNodeData(node);
                }

                this.addAfterNodesNodes();
            });

            // console.log(linksDataArray);

            // this.myDiagram.model.startTransaction('links');
            // (this.myDiagram.model as go.GraphLinksModel).linkDataArray = linksDataArray;
            // this.myDiagram.model.commitTransaction('links');
        }
    }

    public addAfterNodesNodes() {
        if (!this.myDiagram) {
            return;
        }

        const cables = this.myDiagram.model.nodeDataArray.filter(node => node.group === 'col-three' && node.category === 'cableNodeGroupTemplate');

        console.log('Cables:', cables);
        const cores = this.myDiagram.model.nodeDataArray.filter(node => !node.isGroup && node.category === 'coreNodeTemplate');

        const allAfterNodes = this.myDiagram.model.nodeDataArray.filter(node => node.category === 'afterNodeAddButtonTemplate');
        for (const node of allAfterNodes) {
            this.myDiagram.model.removeNodeData(node);
        }

        setTimeout(() => {
            if (!this.myDiagram) return;
            // Handle IN_CAPS
            if (cables.length > 0) {
                this.myDiagram.model.addNodeData({
                    key: `afterCable-${cables[0].key}`,
                    group: 'col-three',
                    category: 'afterNodeAddButtonTemplate',
                    sortOrder: 0.5,
                    type: 'afterCable',
                    where: 'before',
                    cableId: cables[0].key,
                });
                for (const cable of cables) {
                    this.myDiagram.model.addNodeData({
                        key: `afterCable-${cable.key}`,
                        group: `col-three`,
                        category: 'afterNodeAddButtonTemplate',
                        sortOrder: cable.sortOrder ? cable.sortOrder + 0.5 : 0,
                        type: 'afterCable',
                        where: 'after',
                        cableId: cable.key,
                    });

                    const cableCores = cores.filter(core => core.group === cable.key);
                    const maxSortOrder = cableCores.reduce((max, core) => Math.max(max, core.sortOrder || 0), 0);
                    const sortOrderForCorePlusButton = maxSortOrder + 0.5;

                    this.myDiagram.model.addNodeData({
                        key: `afterCore-${cable.key}`,
                        group: cable.key,
                        category: 'afterNodeAddButtonTemplate',
                        sortOrder: sortOrderForCorePlusButton,
                        type: 'afterCore',
                        where: 'after',
                        cableId: cable.key,
                    });
                }
            } else {
                this.myDiagram.model.addNodeData({
                    key: `afterCable-${0}`,
                    group: 'col-three',
                    category: 'afterNodeAddButtonTemplate',
                    sortOrder: 0.5,
                    type: 'afterCable',
                    where: 'before',
                    cableId: null,
                });
            }
        }, 100);
    }

    addSingleCable(cable: CableModel, where: 'before' | 'after') {
        if (!this.hasCircuitEditPermission || this.readonly) return;

        const random = Math.floor(Math.random() * 16777215).toString(16);
        const newCable = new CreateEditCableViewModel({
            title: `Cable ${random}`,
            sortOrder: 0,
        });

        const afterOrder = where === 'before' ? 0 : cable.sortOrder;

        this.LoaderSrv.showFullScreenLoader('Adding cap...');
        this.cablesApi
            .createCablesMultiple(this.selectedConnectionId, afterOrder, [newCable])
            .then(() => {
                this.getLocations().finally(() => {
                    // this.rerenderLinks();
                });
                this.AlertSrv.show('success', 'Cable added successfully!');
            })
            .catch(e => {
                this.AlertSrv.show('error', 'Unable to add cable please try again later!');
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    addSingleCableCore(cable: CableModel) {
        if (!this.hasCircuitEditPermission || this.readonly) return;

        const random = Math.floor(Math.random() * 16777215).toString(16);
        const newCore = new CreateEditCableCoreViewModel({
            title: `Core ${random}`,
            locationConnectionId: this.selectedConnectionId,
        });

        this.LoaderSrv.showFullScreenLoader('Adding core...');
        this.cableCoresApi
            .createCableCoresMultiple(cable.id!, 0, [newCore])
            .then(() => {
                this.getLocations().finally(() => {
                    // this.rerenderLinks();
                });
                this.AlertSrv.show('success', 'Core added successfully!');
            })
            .catch(e => {
                this.AlertSrv.show('error', 'Unable to add core please try again later!');
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    addSingleCoreWithoutCable() {
        if (!this.hasCircuitEditPermission || this.readonly) return;

        const random = Math.floor(Math.random() * 16777215).toString(16);
        const newCore = new CreateEditCableCoreViewModel({
            title: `Core ${random}`,
            locationConnectionId: this.selectedConnectionId,
        });

        this.LoaderSrv.showFullScreenLoader('Adding core...');
        this.cableCoresApi
            .createCoreWithoutCable(newCore)
            .then(() => {
                this.getLocations().finally(() => {
                    // this.rerenderLinks();
                });
                this.AlertSrv.show('success', 'Core added successfully!');
            })
            .catch(e => {
                this.AlertSrv.show('error', 'Unable to add core please try again later!');
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    createSubGridLayout() {
        const $ = go.GraphObject.make;
        return $(go.GridLayout, {
            wrappingColumn: 1, // Two columns for Cap In, Pin In and Pin Out, Cap Out
            alignment: go.GridLayout.Position,
            cellSize: new go.Size(1, 1),
            spacing: new go.Size(20, 10),
        });
    }

    createGroupTemplate = (groupName: string) => {
        const $ = go.GraphObject.make;
        const me = this;

        return $(
            go.Group,
            go.Panel.Vertical,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1, // Ensure nodes are aligned into a single column
                    alignment: go.GridLayout.Position,
                    cellSize: new go.Size(1, 1),
                    spacing: new go.Size(0, 1), // Adjust vertical spacing between nodes
                    comparer: (a: go.Part, b: go.Part) => {
                        const aOrder = a.data.sortOrder ?? 0;
                        const bOrder = b.data.sortOrder ?? 0;
                        return aOrder - bOrder;
                    },
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                mouseDragEnter: (e, group: any) => {
                    group.isHighlighted = true;
                },
                mouseDragLeave: (e, group: any) => {
                    group.isHighlighted = false;
                },
                mouseDrop: (e, group: any) => {
                    if (!me.hasCircuitEditPermission || me.readonly) return;
                    const tool = e.diagram.toolManager.draggingTool;
                    const draggedParts = tool.draggedParts;
                    if (draggedParts) {
                        draggedParts.each(part => {
                            if (part instanceof go.Node) {
                                part.containingGroup = group as any;
                            }
                        });
                    }
                    group.isHighlighted = false;
                    group.layout.invalidateLayout(); // Trigger layout rearrangement

                    // Sort nodeDataArray data of the group based on sortOrder
                    const groupKey = group.data.key;
                    const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

                    groupNodes.sort((a: any, b: any) => {
                        const nodeA = me.myDiagram!.findNodeForData(a);
                        const nodeB = me.myDiagram!.findNodeForData(b);
                        return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
                    });

                    groupNodes.forEach((node: any, index: number) => {
                        // node.sortOrder = index; // Update sortOrder locally
                        e.diagram.model.setDataProperty(node, 'sortOrder', index);
                    });
                },
            },
            $(go.TextBlock, {font: 'bold 12pt sans-serif'}, new go.Binding('text', 'text')),
            $(
                go.Panel,
                go.Panel.Auto,
                $(
                    go.Panel,
                    go.Panel.Auto,
                    $(go.Shape, 'Rectangle', {
                        fill: 'rgba(200, 200, 200, 0.33)',
                        margin: 0,
                        stroke: '#cccccc',
                        strokeWidth: 2,
                    }),
                    new go.Placeholder({padding: 5}) // Add padding inside the rectangle
                )
            )
        );
    };

    public onCableClick(cableId: string) {
        console.log('Cable clicked:', cableId);
        // Find and select the cable
        const cable = this.cables.find(c => c.id === cableId);
        if (cable) {
            this.selectedCable = cable;
            // Clear core and pin selections when cable is selected
            this.selectedCore = null;
            this.selectedPin = null;
        }
    }

    public onCoreClick(coreId: string) {
        console.log('Core clicked:', coreId);
        // Find and select the core
        const allCores = [...this.cables.flatMap(cable => cable.cableCores || []), ...this.noCableCores];
        const core = allCores.find(c => c.id === coreId);
        if (core) {
            this.selectedCore = core;
            // Clear cable and pin selections when core is selected
            this.selectedCable = null;
            this.selectedPin = null;
        }
    }

    public onPinClick(pinId: string) {
        console.log('Pin clicked:', pinId);
        // Find and select the pin
        const allPins = [...this.fromLocationOutPins, ...this.toLocationInPins];
        const pin = allPins.find(p => p.id === pinId);
        if (pin) {
            this.selectedPin = pin;
            // Clear cable and core selections when pin is selected
            this.selectedCable = null;
            this.selectedCore = null;
        }
    }

    // Cable form event handlers
    public onCableCreated(newCable: CableModel): void {
        console.log('Cable created:', newCable);
        // Refresh the diagram to show the new cable
        this.getLocations();
        // Optionally select the new cable
        this.selectedCable = newCable;
    }

    public onCableUpdated(cableId: string): void {
        console.log('Cable updated:', cableId);
        // Refresh the diagram to show the updated cable
        this.getLocations();
    }

    public onFormReset(): void {
        console.log('Cable form reset');
        this.selectedCable = null;
    }

    // Core form event handlers
    public onCoreCreated(newCore: CableCoreModel): void {
        console.log('Core created:', newCore);
        // Refresh the diagram to show the new core
        this.getLocations();
        // Optionally select the new core
        this.selectedCore = newCore;
    }

    public onCoreUpdated(coreId: string): void {
        console.log('Core updated:', coreId);
        // Refresh the diagram to show the updated core
        this.getLocations();
    }

    public onCoreFormReset(): void {
        console.log('Core form reset');
        this.selectedCore = null;
    }

    // Pin form event handlers
    public onPinCreated(newPin: EquipmentPinModel): void {
        console.log('Pin created:', newPin);
        // Refresh the diagram to show the new pin
        this.getLocations();
        // Optionally select the new pin
        this.selectedPin = newPin;
    }

    public onPinUpdated(pinId: string): void {
        console.log('Pin updated:', pinId);
        // Refresh the diagram to show the updated pin
        this.getLocations();
    }

    public onPinFormReset(): void {
        console.log('Pin form reset');
        this.selectedPin = null;
    }

    /**
     * Initializes selected caps for all cables based on existing equipmentInCapId and equipmentOutCapId
     */
    private initializeSelectedCaps(): void {
        this.selectedCaps = {};

        for (const cable of this.cables) {
            this.selectedCaps[cable.id!] = {};

            // Find and set the in cap if equipmentInCapId exists
            if (cable.equipmentInCapId) {
                const inCap = this.toLocationInCaps.find(cap => cap.id === cable.equipmentInCapId);
                if (inCap) {
                    this.selectedCaps[cable.id!].inCap = inCap;
                }
            }

            // Find and set the out cap if equipmentOutCapId exists
            if (cable.equipmentOutCapId) {
                const outCap = this.fromLocationOutCaps.find(cap => cap.id === cable.equipmentOutCapId);
                if (outCap) {
                    this.selectedCaps[cable.id!].outCap = outCap;
                }
            }
        }
    }

    /**
     * Handles cap selection from the select cap dialog
     */
    public onCapSelected(data: {cap: EquipmentCapModel; capType: 'in' | 'out'; cableId: string}): void {
        const {cap, capType, cableId} = data;

        // Initialize selectedCaps for this cable if it doesn't exist
        if (!this.selectedCaps[cableId]) {
            this.selectedCaps[cableId] = {};
        }

        // Update the selected cap
        if (capType === 'in') {
            this.selectedCaps[cableId].inCap = cap;
        } else {
            this.selectedCaps[cableId].outCap = cap;
        }

        // Update the cable model with the new cap selection
        this.updateCableWithSelectedCap(cableId, cap, capType);
    }

    /**
     * Updates the cable model and saves the cap selection to the server
     */
    private async updateCableWithSelectedCap(cableId: string, cap: EquipmentCapModel, capType: 'in' | 'out'): Promise<void> {
        const cable = this.cables.find(c => c.id === cableId);
        if (!cable) {
            console.error('Cable not found for ID:', cableId);
            return;
        }

        try {
            this.LoaderSrv.showFullScreenLoader();

            const updateData = new CreateEditCableViewModel({
                ...cable,
            });

            // Update the appropriate cap ID
            if (capType === 'in') {
                updateData.equipmentInCapId = cap.id;
            } else {
                updateData.equipmentOutCapId = cap.id;
            }

            // Save to server
            await this.cablesApi.updateCable(this.selectedConnectionId, cableId, updateData);

            // Update local model
            if (capType === 'in') {
                cable.equipmentInCapId = cap.id;
            } else {
                cable.equipmentOutCapId = cap.id;
            }

            this.AlertSrv.show('success', `${capType === 'in' ? 'Input' : 'Output'} cap connected successfully!`);

            this.getLocations();
        } catch (error) {
            console.error('Failed to update cable cap connection:', error);
            this.AlertSrv.show('error', 'Failed to save cap connection. Please try again.');

            // Revert the local selectedCaps change
            if (capType === 'in') {
                delete this.selectedCaps[cableId].inCap;
            } else {
                delete this.selectedCaps[cableId].outCap;
            }

            this.getLocations();
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    /**
     * Handles when a new cap is added
     */
    public onCapAdded(cap: EquipmentCapModel): void {
        try {
            // Determine which location this cap belongs to and update the appropriate cap list
            if (cap.capDirection === CAP_DIRECTION.IN) {
                // Input cap - add to toLocation caps
                if (this.toLocation && cap.locationId === this.toLocation.id) {
                    this.toLocationInCaps.push(cap);
                    // Sort by sort order
                    this.toLocationInCaps.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                }
            } else {
                // Output cap - add to fromLocation caps
                if (this.fromLocation && cap.locationId === this.fromLocation.id) {
                    this.fromLocationOutCaps.push(cap);
                    // Sort by sort order
                    this.fromLocationOutCaps.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                }
            }

            // Update the diagram to reflect the new cap
            this.updateNodeDataArray();
        } catch (error) {
            console.error('Error handling cap added event:', error);
        }
    }

    /**
     * Handles when a cap is deleted
     */
    public onCapDeleted(cap: EquipmentCapModel): void {
        try {
            // Remove the cap from the appropriate cap list
            if (cap.capDirection === CAP_DIRECTION.IN) {
                // Input cap - remove from toLocation caps
                const index = this.toLocationInCaps.findIndex(c => c.id === cap.id);
                if (index > -1) {
                    this.toLocationInCaps.splice(index, 1);
                }
            } else {
                // Output cap - remove from fromLocation caps
                const index = this.fromLocationOutCaps.findIndex(c => c.id === cap.id);
                if (index > -1) {
                    this.fromLocationOutCaps.splice(index, 1);
                }
            }

            // Remove any cable connections that were using this cap
            this.cables.forEach(cable => {
                if (cable.equipmentInCapId === cap.id) {
                    cable.equipmentInCapId = null;
                    // Remove from selectedCaps if present
                    if (this.selectedCaps[cable.id!]?.inCap?.id === cap.id) {
                        delete this.selectedCaps[cable.id!].inCap;
                    }
                }
                if (cable.equipmentOutCapId === cap.id) {
                    cable.equipmentOutCapId = null;
                    // Remove from selectedCaps if present
                    if (this.selectedCaps[cable.id!]?.outCap?.id === cap.id) {
                        delete this.selectedCaps[cable.id!].outCap;
                    }
                }
            });

            this.getLocations(); // Refresh the diagram to reflect changes
        } catch (error) {
            console.error('Error handling cap deleted event:', error);
        }
    }

    /**
     * Opens the select cap dialog for a specific cable and cap type
     */
    public openSelectCapDialog(cableId: string, capType: 'in' | 'out'): void {
        const selectedCable = this.cables.find(c => c.id === cableId);
        let currentCapId: string | null = null;

        if (selectedCable) {
            currentCapId = capType === 'in' ? selectedCable.equipmentInCapId || '' : selectedCable.equipmentOutCapId || '';
        } else {
            const noCableCore = this.noCableCores.find(core => core.id === cableId);
            if (noCableCore) {
                currentCapId = capType === 'in' ? noCableCore.equipmentInCapId || '' : noCableCore.equipmentOutCapId || '';
            }
        }

        this.$refs.selectCapDialogRef?.openDialog({
            capType,
            currentCapId,
            cableId,
            coreId: cableId,
            locationId: capType === 'in' ? this.toLocation?.id || undefined : this.fromLocation?.id || undefined,
        });
    }

    /**
     * Gets the display text for a selected cap
     */
    public getSelectedCapText(cableId: string, capType: 'in' | 'out'): string {
        const selectedCap = capType === 'in' ? this.selectedCaps[cableId]?.inCap : this.selectedCaps[cableId]?.outCap;

        const selectCapText = capType === 'in' ? 'Select In Cap' : 'Select Out Cap';
        const selectedCapText = capType === 'in' ? 'Selected In Cap' : 'Selected Out Cap';

        return selectedCap ? selectedCap.title || selectedCapText : selectCapText;
    }
}
